import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiCheck, FiEdit3, FiImage, FiSliders, FiUploadCloud, FiX } from "react-icons/fi";
import type { ImageFieldValue, ImageOptions } from "@bcc/core-renderers";
import { Modal } from "./Modal";
import { client } from "../Clients";
import type { StorageObject } from "../API";
import { resolveStorageUrl as resolveStorageUrlHelper } from "../Helper/storage";
import { normalizeStorageKey } from "../Helper/storage";
import ImageOptionsModal, { createDefaultImageOptions, normalizeImageOptions } from "./ImageOptionsModal";
import { MediaLibrary } from "./MediaLibrary";
import TaxonomyMultiSelect from "./TaxonomyMultiSelect";
import type { IApplicationState } from "../Redux/Reducers";

type ImagePickerInputProps = {
	value?: ImageFieldValue | null;
	onChange: (next?: ImageFieldValue | null) => void;
	label?: string;
	helper?: string;
	disabled?: boolean;
	folder?: string;
	className?: string;
};

type UploadFormState = {
	title: string;
	fileName: string;
	folder: string;
	taxonomyIds: string[];
	file: File | null;
};

const DEFAULT_FOLDER = "images";

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const buildPublicUrl = (value?: string | null): string | null => {
	if (!value) return null;
	const resolved = resolveStorageUrlHelper(value);
	return resolved ? resolved : null;
};

// Prefer the compressed rendition when the upload pipeline produced one : that is the key pages
// should reference; the original stays in storage but is not what we want on the public site.
const resolveAssetKey = (asset?: StorageObject | null): string =>
	normalizeStorageKey((asset?.compressedKey ?? asset?.key ?? "").toString());

const initialUploadState: UploadFormState = {
	title: "",
	fileName: "",
	folder: "",
	taxonomyIds: [],
	file: null
};

const resolveImageValue = (value?: ImageFieldValue | null): { src: string; options?: ImageOptions } => {
	if (!value) return { src: "" };
	if (typeof value === "string") return { src: value };
	return { src: value.src ?? "", options: value.options };
};

const isValidNumber = (value?: number) => typeof value === "number" && Number.isFinite(value);

const normalizeIds = (ids?: Array<string | null>) =>
	(ids ?? []).filter((id): id is string => Boolean(id && id.trim())).map((id) => id.trim()).sort();

const sameIdList = (a?: Array<string | null>, b?: Array<string | null>) => {
	const left = normalizeIds(a);
	const right = normalizeIds(b);
	if (left.length !== right.length) return false;
	return left.every((value, index) => value === right[index]);
};

const buildTaxonomyLabel = (id: string, map: Map<string, string>) => map.get(id) ?? id;

// Persist only what deviates from the defaults: disabled toggles, zero focal-point offsets and
// non-finite numbers are all dropped, so stored JSON never carries implicit settings. Combined
// with buildImageValue below, an image with no surviving options collapses back to a bare string
// key : the most common case stays the smallest shape.
const compactImageOptions = (value?: ImageOptions | null): ImageOptions | undefined => {
	if (!value) return undefined;
	const next: ImageOptions = {};

	if (value.center?.enabled) {
		const center: ImageOptions["center"] = { enabled: true };
		if (isValidNumber(value.center.x) && value.center.x !== 0) {
			center.x = value.center.x;
		}
		if (isValidNumber(value.center.y) && value.center.y !== 0) {
			center.y = value.center.y;
		}
		next.center = center;
	}

	const overrides = value.overrides;
	if (overrides) {
		const compactedOverrides: NonNullable<ImageOptions["overrides"]> = {};
		const entries: Array<["mobile" | "tablet" | "desktop", ImageOptions["overrides"]["mobile"]]> = [
			["mobile", overrides.mobile],
			["tablet", overrides.tablet],
			["desktop", overrides.desktop]
		];

		entries.forEach(([label, override]) => {
			if (!override?.enabled) return;
			const nextOverride: ImageOptions["overrides"]["mobile"] = { enabled: true };
			const minHeight = override.minHeight;
			if (minHeight?.enabled && isValidNumber(minHeight.value)) {
				nextOverride.minHeight = { enabled: true, value: minHeight.value };
			}
			const maxHeight = override.maxHeight;
			if (maxHeight?.enabled && isValidNumber(maxHeight.value)) {
				nextOverride.maxHeight = { enabled: true, value: maxHeight.value };
			}
			const height = override.height;
			if (height?.enabled && isValidNumber(height.value)) {
				nextOverride.height = { enabled: true, value: height.value };
			}
			compactedOverrides[label] = nextOverride;
		});

		if (Object.keys(compactedOverrides).length > 0) {
			next.overrides = compactedOverrides;
		}
	}

	const fit = (value as ImageOptions & { fit?: string }).fit;
	if (fit) {
		(next as ImageOptions & { fit?: string }).fit = fit;
	}

	return Object.keys(next).length > 0 ? next : undefined;
};

const buildImageValue = (src?: string | null, options?: ImageOptions | null): ImageFieldValue | null => {
	const normalized = normalizeStorageKey(src);
	if (!normalized) return null;
	const compacted = compactImageOptions(options);
	return compacted ? { src: normalized, options: compacted } : normalized;
};

/**
 * Image field editor: a compact key display with a full-screen browse/upload modal plus an
 * options ("Custom") modal for focal point and responsive height overrides. Intentionally
 * parallel to FilePickerInput : the two stay separate components because they differ in value
 * contract, not just chrome: this one emits an ImageFieldValue (bare storage key string, or
 * { src, options } only when options exist), while the file picker emits an HrefTarget with an
 * absolute public URL.
 *
 * Browsing is delegated to the shared MediaLibrary (mode "pick", accept "images" : local nav,
 * the host page URL is never touched). Selection stays staged: a pick lands in
 * pendingKey/pendingAsset and nothing reaches `onChange` until Confirm : or a second pick of the
 * same tile, which replaces the old double-click shortcut. The taxonomy panel is the exception : * it edits the storage object itself and saves immediately via the storage API, independent of
 * whether the image ends up selected.
 */
const ImagePickerInput: React.FC<ImagePickerInputProps> = ({
	value,
	onChange,
	label,
	helper,
	disabled = false,
	folder = DEFAULT_FOLDER,
	className = ""
}) => {
	const [open, setOpen] = useState(false);
	const [customOpen, setCustomOpen] = useState(false);
	const [pendingKey, setPendingKey] = useState<string>("");
	const [pendingAsset, setPendingAsset] = useState<StorageObject | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
	const [uploadForm, setUploadForm] = useState<UploadFormState>(initialUploadState);
	const [optionsDraft, setOptionsDraft] = useState<ImageOptions>(createDefaultImageOptions());
	// Fait recharger la MediaLibrary embarquée (après un upload réussi depuis ce panneau).
	const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
	const [taxonomyDraftIds, setTaxonomyDraftIds] = useState<string[]>([]);
	const [taxonomySaving, setTaxonomySaving] = useState(false);
	const [taxonomyMessage, setTaxonomyMessage] = useState<string | null>(null);
	const [taxonomyEditing, setTaxonomyEditing] = useState(false);

	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);

	const { src: currentSrc, options: currentOptions } = useMemo(() => resolveImageValue(value), [value]);
	const normalizedValue = useMemo(() => normalizeStorageKey(currentSrc), [currentSrc]);

	const displayValue = useMemo(() => {
		if (!normalizedValue) return "No image selected";
		return normalizedValue;
	}, [normalizedValue]);

	const previewUrl = useMemo(() => buildPublicUrl(pendingKey || normalizedValue || null), [pendingKey, normalizedValue]);
	const canUpload = Boolean(uploadForm.title.trim()) && Boolean(uploadForm.file);
	const hasPendingSelection = Boolean(pendingKey.trim());
	const hasImage = Boolean(normalizedValue.trim());

	// Opening the modal resets all staging state and seeds the pending selection with the current
	// value, so "open then Confirm" is a no-op rather than a clear.
	useEffect(() => {
		if (!open) return;
		setPendingKey(normalizedValue);
		setPendingAsset(null);
		setUploadForm(initialUploadState);
		setUploadError(null);
		setUploadSuccess(null);
		setTaxonomyDraftIds([]);
		setTaxonomyMessage(null);
		setTaxonomyEditing(false);
	}, [open, normalizedValue]);

	// The committed value is just a key; back-fill the matching StorageObject (by-key resolves any
	// of the three key variants) so the taxonomy panel can show and edit its tags. Absolute URLs
	// and `{variable}` placeholders are skipped : a failed lookup simply leaves the panel closed.
	// Never overwrites an explicit tile selection.
	useEffect(() => {
		if (!open || !pendingKey || pendingAsset) return;
		const normalized = normalizeStorageKey(pendingKey);
		if (!normalized || isAbsoluteUrl(normalized)) return;
		let cancelled = false;
		client.storage
			.apiStorageByKeyGet(normalized)
			.then((response) => {
				if (!cancelled && response.data) setPendingAsset(response.data);
			})
			.catch(() => {
				/* clé introuvable (placeholder, fichier supprimé) : panneau taxonomies simplement absent */
			});
		return () => {
			cancelled = true;
		};
	}, [open, pendingAsset, pendingKey]);

	useEffect(() => {
		if (!pendingAsset) {
			setTaxonomyDraftIds([]);
			setTaxonomyEditing(false);
			return;
		}
		setTaxonomyDraftIds(normalizeIds(pendingAsset.taxonomyIds));
		setTaxonomyEditing(false);
	}, [pendingAsset]);

	const handleSelectAsset = (asset: StorageObject) => {
		const key = resolveAssetKey(asset);
		setPendingKey(key);
		setPendingAsset(asset);
	};

	// Pick depuis la MediaLibrary : premier clic = mise en attente ; re-cliquer la vignette déjà
	// sélectionnée confirme directement (remplace l'ancien double-clic du navigateur interne).
	const handlePick = (asset: StorageObject) => {
		const key = resolveAssetKey(asset);
		if (!key) return;
		if (key === normalizeStorageKey(pendingKey)) {
			handleConfirm(key);
			return;
		}
		handleSelectAsset(asset);
	};

	const taxonomyDirty = useMemo(
		() => (pendingAsset ? !sameIdList(pendingAsset.taxonomyIds ?? [], taxonomyDraftIds) : false),
		[pendingAsset, taxonomyDraftIds]
	);

	const handleSaveTaxonomies = async () => {
		if (!pendingAsset?.id || !taxonomyDirty) return;
		setTaxonomySaving(true);
		setTaxonomyMessage(null);
		try {
			const response = await client.storage.apiStorageIdTaxonomiesPut(pendingAsset.id, {
				taxonomyIds: taxonomyDraftIds
			});
			const updated = response.data ?? { ...pendingAsset, taxonomyIds: taxonomyDraftIds };
			setPendingAsset(updated);
			setLibraryRefreshKey((token) => token + 1);
			setTaxonomyMessage("Taxonomies updated.");
			setTaxonomyEditing(false);
		} catch (err) {
			console.error(err);
			setTaxonomyMessage("Unable to update taxonomies.");
		} finally {
			setTaxonomySaving(false);
		}
	};

	const handleClear = () => {
		onChange(null);
		setPendingKey("");
		setPendingAsset(null);
		setOpen(false);
	};

	// Swapping the image keeps the existing crop/height options : they usually describe the slot
	// (hero, card...) rather than the specific picture, so carrying them over is the useful default.
	const handleConfirm = (overrideKey?: string) => {
		const key = (overrideKey ?? pendingKey).trim();
		if (!key) return;
		const normalized = normalizeStorageKey(key);
		onChange(buildImageValue(normalized, currentOptions));
		setOpen(false);
	};

	const handleOpenCustom = () => {
		setOptionsDraft(normalizeImageOptions(currentOptions));
		setCustomOpen(true);
	};

	const handleSaveOptions = () => {
		if (!hasImage) return;
		onChange(buildImageValue(currentSrc, optionsDraft));
		setCustomOpen(false);
	};

	const handleResetOptions = () => {
		setOptionsDraft(createDefaultImageOptions());
	};

	const handleUploadInput = (key: keyof UploadFormState, value: string | string[] | File | null) => {
		setUploadForm((prev) => ({
			...prev,
			[key]: value as never
		}));
	};

	// Prefill title/fileName from the picked file, but never overwrite what the user already typed.
	const handleFileChange = (file: File | null) => {
		handleUploadInput("file", file);
		setUploadSuccess(null);
		setUploadError(null);
		if (!file) return;
		if (!uploadForm.fileName) {
			const base = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
			handleUploadInput("fileName", base);
		}
		if (!uploadForm.title) {
			handleUploadInput("title", file.name.replace(/\.[^/.]+$/, ""));
		}
	};

	const handleUpload = async () => {
		if (!uploadForm.file) return;
		setUploading(true);
		setUploadError(null);
		setUploadSuccess(null);
		try {
			const taxonomyFilter = uploadForm.taxonomyIds.length ? uploadForm.taxonomyIds.join(",") : undefined;
			const response = await client.storage.apiStorageUploadImagesPost(
				uploadForm.title.trim(),
				uploadForm.file,
				uploadForm.fileName.trim() || undefined,
				uploadForm.folder.trim() || undefined,
				taxonomyFilter
			);
			const created = response.data ?? null;
			setUploadSuccess("Image uploaded successfully.");
			setUploadForm(initialUploadState);
			if (created?.key || created?.compressedKey) {
				const key = normalizeStorageKey(created.compressedKey ?? created.key ?? "");
				setPendingKey(key);
				setPendingAsset(created);
			}
			setLibraryRefreshKey((token) => token + 1);
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.message ?? err?.response?.data ?? null;
			setUploadError(apiMessage ? `Upload failed: ${apiMessage}` : "Upload failed. Please try again.");
		} finally {
			setUploading(false);
		}
	};

	return (
		<div className={`space-y-1 ${className}`}>
			{label && <label className="text-xs font-semibold text-slate-600">{label}</label>}
			<div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[15px] text-slate-800 shadow-sm transition hover:border-slate-300">
				<FiImage className="text-slate-500" />
				<div className="flex-1 min-w-0 truncate">{displayValue}</div>
				{!disabled && (
					<>
						{hasImage && (
							<button
								type="button"
								onClick={handleClear}
								className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
								aria-label="Clear image"
							>
								<FiX />
							</button>
						)}
						<button
							type="button"
							onClick={() => setOpen(true)}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
						>
							<FiEdit3 /> Edit
						</button>
						<button
							type="button"
							onClick={handleOpenCustom}
							disabled={!hasImage}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							<FiSliders /> Custom
						</button>
					</>
				)}
			</div>
			{helper && <p className="text-[11px] text-slate-500">{helper}</p>}

			<Modal show={open} onHide={() => setOpen(false)} size="full85" centered>
				<Modal.Header className="bg-slate-900 text-white" closeButton>
					<div className="flex items-center gap-2">
						<FiImage />
						<Modal.Title>Select an image</Modal.Title>
					</div>
				</Modal.Header>
				<Modal.Body className="bg-slate-50">
					<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
						{/* Navigateur partagé : mode pick : navigation locale, l'URL de la page n'est pas touchée. */}
						<div className="min-w-0">
							<MediaLibrary
								mode="pick"
								accept="images"
								onPick={handlePick}
								refreshKey={libraryRefreshKey}
								initialPath={folder !== DEFAULT_FOLDER ? folder : undefined}
							/>
						</div>

						<div className="space-y-4">
							<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Preview</p>
										<p className="text-sm text-slate-600">Selected image preview.</p>
									</div>
									{hasPendingSelection && (
										<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800">
											<FiCheck /> Selected
										</span>
									)}
								</div>
								<div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
									{previewUrl ? (
										<img src={previewUrl} alt={pendingAsset?.title ?? "Selected image"} className="mx-auto max-h-[240px] w-full rounded-lg object-contain" />
									) : (
										<div className="text-center text-sm text-slate-500">No image selected yet.</div>
									)}
								</div>
								{pendingKey && (
									<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 break-all">
										{normalizeStorageKey(pendingKey)}
									</div>
								)}
							</div>

							{pendingAsset && (
								<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
									<div className="flex items-center justify-between">
										<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
										<button
											type="button"
											onClick={() => {
												if (!taxonomyEditing) {
													setTaxonomyEditing(true);
													return;
												}
												handleSaveTaxonomies();
											}}
											disabled={(taxonomyEditing ? !taxonomyDirty : false) || taxonomySaving || !pendingAsset.id}
											className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
										>
											{taxonomySaving ? "Saving…" : taxonomyEditing ? "Save" : "Edit"}
										</button>
									</div>
									{taxonomyEditing ? (
										<TaxonomyMultiSelect
											options={taxonomies}
											value={taxonomyDraftIds}
											onChange={setTaxonomyDraftIds}
											placeholder="Assign taxonomies"
											helper="Filter and select taxonomies for this image."
										/>
									) : (
										<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
											{pendingAsset.taxonomyIds?.length
												? pendingAsset.taxonomyIds.map((id) => buildTaxonomyLabel(id, taxonomyMap)).join(", ")
												: "No taxonomies assigned."}
										</div>
									)}
									{taxonomyMessage && (
										<p className="text-xs text-slate-500">{taxonomyMessage}</p>
									)}
								</div>
							)}

							<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
								<div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
									<FiUploadCloud /> Upload new image
								</div>
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">Title</label>
									<input
										type="text"
										className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										placeholder="Homepage hero"
										value={uploadForm.title}
										onChange={(event) => handleUploadInput("title", event.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">File</label>
									<input
										type="file"
										accept="image/*"
										onChange={(event) => handleFileChange(event.target.files ? event.target.files[0] : null)}
										className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
									/>
									{uploadForm.file && <p className="text-xs text-slate-500">Selected: {uploadForm.file.name}</p>}
								</div>
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">File name (optional)</label>
									<input
										type="text"
										className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										value={uploadForm.fileName}
										onChange={(event) => handleUploadInput("fileName", event.target.value)}
									/>
								</div>
								<div className="grid gap-3 sm:grid-cols-2">
									<div className="space-y-2">
										<label className="text-xs font-semibold text-slate-600">Folder (optional)</label>
										<input
											type="text"
											className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
											placeholder="home"
											value={uploadForm.folder}
											onChange={(event) => handleUploadInput("folder", event.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<TaxonomyMultiSelect
											label="Taxonomies"
											options={taxonomies}
											value={uploadForm.taxonomyIds}
											onChange={(next) => handleUploadInput("taxonomyIds", next)}
											placeholder="Assign taxonomies"
											helper="Select one or more taxonomies for this image."
										/>
									</div>
								</div>
								{uploadError && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{uploadError}</p>}
								{uploadSuccess && <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{uploadSuccess}</p>}
								<button
									type="button"
									disabled={!canUpload || uploading}
									onClick={handleUpload}
									className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
								>
									{uploading ? "Uploading..." : "Upload image"}
								</button>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer className="bg-white">
					<div className="flex flex-1 items-center justify-between gap-2">
						<button
							type="button"
							onClick={handleClear}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
						>
							<FiX /> Clear
						</button>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
							>
								Close
							</button>
							<button
								type="button"
									onClick={() => handleConfirm()}
								disabled={!hasPendingSelection}
								className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
							>
								<FiCheck /> Confirm
							</button>
						</div>
					</div>
				</Modal.Footer>
			</Modal>

			<ImageOptionsModal
				show={customOpen}
				value={optionsDraft}
				onChange={setOptionsDraft}
				onCancel={() => setCustomOpen(false)}
				onSave={handleSaveOptions}
				onReset={handleResetOptions}
				saveDisabled={!hasImage}
			/>
		</div>
	);
};

export default ImagePickerInput;
